import React, { useState, useEffect } from 'react';
import { Card, Tabs, Form, Input, Button, Row, Col, Typography, Avatar, Divider, Space, message, Alert } from 'antd';
import { UserOutlined, LockOutlined, SaveOutlined, EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { useAuthStore } from '../store/useAuthStore';

const { Title, Text } = Typography;
const { Password } = Input;

const Profile = () => {
  const { user ,updateUser } = useAuthStore();
  const [generalForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  useEffect(() => {
    if (user) {
      generalForm.setFieldsValue({
        name: user.name,
        email: user.email,
        department: user.department,
        position: user.position,
        role: user.role
      });
    }
  }, [user, generalForm]);

  const handleGeneralUpdate = async (values) => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/employees/${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(values),
      });

      const data = await response.json();
      
      if (data.success) {
        useAuthStore.getState().updateUser(values);
        message.success('Profile updated successfully');
      } else {
        message.error(data.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Profile update error:', error);
      message.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (values) => {
    setPasswordLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.id,
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        message.success('Password updated successfully');
        passwordForm.resetFields();
      } else {
        message.error(data.message || 'Failed to update password');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      message.error('Failed to update password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const GeneralTab = () => (
    <Card>
      <div className="mb-6 text-center">
        <Avatar size={80} icon={<UserOutlined />} className="mb-4" />
        <Title level={3} className="mb-1">{user?.name}</Title>
        <Text type="secondary">{user?.position} • {user?.department}</Text>
      </div>

      <Divider />

      <div className="mb-4 flex justify-end">
        <Button 
          type="primary" 
          icon={<SaveOutlined />} 
          onClick={() => generalForm.submit()}
          loading={loading}
          size="large"
        >
          Update Profile
        </Button>
      </div>

      <Form
        form={generalForm}
        layout="vertical"
        onFinish={handleGeneralUpdate}
        size="large"
      >
        <Row gutter={[24, 16]}>
          <Col xs={24} md={12}>
            <Form.Item
              name="name"
              label="Full Name"
              rules={[{ required: true, message: 'Please enter your name' }]}
            >
              <Input placeholder="Enter your full name" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="email"
              label="Email Address"
              rules={[
                { required: true, message: 'Please enter your email' },
                { type: 'email', message: 'Please enter a valid email' }
              ]}
            >
              <Input placeholder="Enter your email address" disabled />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[24, 16]}>
          <Col xs={24} md={12}>
            <Form.Item
              name="department"
              label="Department"
            >
              <Input placeholder="Department" disabled />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="position"
              label="Position"
            >
              <Input placeholder="Position" disabled />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[24, 16]}>
          <Col xs={24} md={12}>
            <Form.Item
              name="role"
              label="Role"
            >
              <Input placeholder="Role" disabled />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>
  );

  const PasswordTab = () => (
    <Card>
      <div className="mb-6">
        <Title level={4}>Change Password</Title>
        <Text type="secondary">
          Update your password to keep your account secure. Make sure to use a strong password.
        </Text>
      </div>

      <Alert
        message="Password Requirements"
        description="Password must be at least 6 characters long and contain a mix of letters and numbers."
        type="info"
        showIcon
        className="mb-6"
      />

      <div className="mb-4 flex justify-end">
        <Button 
          type="primary" 
          icon={<LockOutlined />} 
          onClick={() => passwordForm.submit()}
          loading={passwordLoading}
          size="large"
        >
          Update Password
        </Button>
      </div>

      <Form
        form={passwordForm}
        layout="vertical"
        onFinish={handlePasswordReset}
        size="large"
      >
        <Row gutter={[24, 16]}>
          <Col xs={24} md={12}>
            <Form.Item
              name="currentPassword"
              label="Current Password"
              rules={[{ required: true, message: 'Please enter your current password' }]}
            >
              <Password
                placeholder="Enter current password"
                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[24, 16]}>
          <Col xs={24} md={12}>
            <Form.Item
              name="newPassword"
              label="New Password"
              rules={[
                { required: true, message: 'Please enter a new password' },
                { min: 6, message: 'Password must be at least 6 characters long' }
              ]}
            >
              <Password
                placeholder="Enter new password"
                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                onKeyDown={(e) => {
                    if (e.key === ' ') {
                      e.preventDefault(); // block space key
                    }
                  }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="confirmPassword"
              label="Confirm New Password"
              dependencies={['newPassword']}
              rules={[
                { required: true, message: 'Please confirm your new password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match'));
                  },
                }),
              ]}
            >
              <Password
                placeholder="Confirm new password"
                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                onKeyDown={(e) => {
                  if (e.key === ' ') {
                    e.preventDefault(); // block space key
                  }
                }}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>
  );

  const tabItems = [
    {
      key: 'general',
      label: (
        <span>
          <UserOutlined />
          General
        </span>
      ),
      children: <GeneralTab />
    },
    {
      key: 'password',
      label: (
        <span>
          <LockOutlined />
          Password
        </span>
      ),
      children: <PasswordTab />
    }
  ];

  return (
    <div className="p-6">
      <div className="mb-6">
        <Title level={2}>
          <UserOutlined className="mr-2" />
          My Profile
        </Title>
        <Text type="secondary">
          Manage your account settings and preferences
        </Text>
      </div>

      <Tabs
        defaultActiveKey="general"
        items={tabItems}
        size="large"
      />
    </div>
  );
};

export default Profile;
